import { cn } from '@/lib/utils';
import { ORDER_JOURNEY_STEPS, getOrderProgress } from './order-journey';
import type { Order } from '@/types/domain';

interface OrderJourneyProps {
  order: Pick<Order, 'status' | 'packages_json'> | null | undefined;
  /** "horizontal" for the sheet, "vertical" for the full page. */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

/**
 * Shared order lifecycle stepper.
 *
 * Colour note: completed steps use `success`, the current step uses `primary`.
 * ViewOrder currently paints the current step with `success`, which collides
 * with green = Confirmed in the badge system. Primary (teal) is the system's
 * "you are here" colour, so that collision is resolved here.
 */
export function OrderJourney({
  order,
  orientation = 'horizontal',
  className,
}: OrderJourneyProps) {
  const progress = getOrderProgress(order);

  if (orientation === 'vertical') {
    return (
      <ol className={cn('relative flex flex-col', className)}>
        {ORDER_JOURNEY_STEPS.map((step, index) => {
          const isCompleted = index < progress;
          const isCurrent = index === progress;
          const StepIcon = step.icon;
          return (
            <li key={step.key} className="relative flex gap-3 pb-5 last:pb-0">
              {index < ORDER_JOURNEY_STEPS.length - 1 && (
                <span
                  aria-hidden
                  className={cn(
                    'absolute left-[15px] top-8 h-[calc(100%-16px)] w-0.5',
                    isCompleted ? 'bg-success' : 'bg-border',
                  )}
                />
              )}
              <span
                className={cn(
                  'relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full',
                  isCompleted && 'bg-success/15 text-success',
                  isCurrent &&
                    'bg-primary text-primary-foreground shadow-[0_0_12px_color-mix(in_srgb,var(--primary)_45%,transparent)]',
                  !isCompleted && !isCurrent && 'bg-muted text-muted-foreground',
                )}
              >
                <StepIcon size={15} />
              </span>
              <div className="min-w-0 pt-1">
                <p
                  className={cn(
                    'text-sm font-medium',
                    isCurrent
                      ? 'text-foreground'
                      : isCompleted
                        ? 'text-foreground'
                        : 'text-muted-foreground',
                  )}
                >
                  {step.label}
                </p>
                {isCurrent && (
                  <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-primary">
                    Current
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    );
  }

  return (
    <ol className={cn('flex items-start', className)}>
      {ORDER_JOURNEY_STEPS.map((step, index) => {
        const isCompleted = index < progress;
        const isCurrent = index === progress;
        const StepIcon = step.icon;
        return (
          <li
            key={step.key}
            className="flex min-w-0 flex-1 flex-col items-center gap-1.5 last:flex-none"
          >
            <div className="flex w-full items-center">
              <span
                className={cn(
                  'flex size-7 shrink-0 items-center justify-center rounded-full',
                  isCompleted && 'bg-success/15 text-success',
                  isCurrent &&
                    'bg-primary text-primary-foreground shadow-[0_0_10px_color-mix(in_srgb,var(--primary)_45%,transparent)]',
                  !isCompleted && !isCurrent && 'bg-muted text-muted-foreground',
                )}
                title={step.label}
              >
                <StepIcon size={13} />
              </span>
              {index < ORDER_JOURNEY_STEPS.length - 1 && (
                <span
                  aria-hidden
                  className={cn(
                    'mx-1 h-0.5 min-w-2 flex-1',
                    isCompleted ? 'bg-success' : 'bg-border',
                  )}
                />
              )}
            </div>
            <span
              className={cn(
                'w-full pr-2 text-left text-[10px] leading-tight',
                isCurrent
                  ? 'font-medium text-foreground'
                  : isCompleted
                    ? 'text-muted-foreground'
                    : 'text-muted-foreground/60',
              )}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default OrderJourney;
